/**
 * Copies of PublicMatchAnalytics' module-private `mapApiMatch` /
 * `mapApiEvent` (that file is intentionally untouched: PMA-BCAST-006).
 * Keep in sync by hand if the shared page's mapping changes.
 */
import type { MatchEvent } from "../../store/useMatchLogStore";
import type { Match, Player, StaffMember, Team } from "../logger/types";

const normalizeComparableId = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .toUpperCase();

export function mapApiMatch(raw: Record<string, any>): Match {
  const mapPlayer = (p: Record<string, any>): Player => ({
    id: p.player_id ?? p.id ?? "",
    full_name: p.player_name ?? p.full_name ?? "",
    short_name: p.short_name,
    jersey_number: p.jersey_number ?? 0,
    position: p.position ?? "",
    birth_date: p.birth_date ?? null,
    is_starter: p.is_starter ?? true,
    photo_url: p.photo_url ?? null,
    photo_image: p.photo_image ?? null,
  });
  const mapStaff = (s: Record<string, any>): StaffMember => ({
    staff_id: s.staff_id ?? "",
    name: s.name ?? "",
    role: String(s.role ?? ""),
  });
  const mapTeam = (t: Record<string, any>): Team => ({
    id: normalizeComparableId(t.team_id),
    team_id: normalizeComparableId(t.team_id),
    name: t.name ?? "",
    short_name: t.short_name ?? t.name?.slice(0, 3)?.toUpperCase() ?? "",
    score: t.score ?? 0,
    logo_url: t.logo_url ?? null,
    logo_image: t.logo_image ?? null,
    players: (t.lineup ?? []).map(mapPlayer),
    technical_staff: (t.technical_staff ?? [])
      .filter((s: Record<string, any>) => Boolean(s?.staff_id))
      .map(mapStaff),
    primary_color: t.primary_color ?? null,
    secondary_color: t.secondary_color ?? null,
    kit: t.kit ?? null,
  });

  return {
    id: raw.match_id ?? raw._id ?? "",
    home_team: mapTeam(raw.home_team ?? {}),
    away_team: mapTeam(raw.away_team ?? {}),
    status: raw.status ?? "Pending",
    match_time_seconds: raw.match_time_seconds ?? 0,
    current_period_start_timestamp:
      raw.current_period_start_timestamp ?? undefined,
    clock_seconds_at_period_start:
      raw.clock_seconds_at_period_start ?? undefined,
    period_timestamps: raw.period_timestamps ?? {},
    ineffective_time_seconds: raw.ineffective_time_seconds ?? 0,
    ineffective_aggregates: raw.ineffective_aggregates ?? null,
    clock_mode: raw.clock_mode ?? "EFFECTIVE",
    last_mode_change_timestamp: raw.last_mode_change_timestamp ?? undefined,
  };
}

export function mapApiEvent(raw: Record<string, any>): MatchEvent {
  return {
    _id: raw._id ?? undefined,
    match_id: raw.match_id ?? "",
    timestamp: raw.timestamp ?? "",
    client_id: raw.client_id ?? undefined,
    match_clock: raw.match_clock ?? "00:00.000",
    period: raw.period ?? 1,
    team_id: normalizeComparableId(raw.team_id),
    player_id: raw.player_id ?? undefined,
    staff_id: raw.staff_id ?? undefined,
    location: raw.location ?? undefined,
    type: raw.type ?? "Pass",
    data: raw.data ?? {},
    notes: raw.notes ?? null,
    _confirmed: true,
  };
}
